/**
 * Finding things by name: parts, systems and FMA concepts, ranked.
 *
 * The atlas has 2,234 parts, 15 systems and 3,432 concepts, so browsing alone
 * is not enough to reach a named structure. This is a small ranked prefix
 * search over the terms the catalogue indexed once at load. Pure, so the
 * ranking is testable and does not need a headset to tune.
 */
import type { Catalog, Group } from "../model.js";

export interface SearchHit {
  group: Group;
  score: number;
  /** Parts the hit would show, for the count in the result row. */
  count: number;
}

export interface SearchOptions {
  limit?: number;
  /** Restrict to groups whose parts are in these systems. */
  systems?: ReadonlySet<string>;
}

const WORD = /[^a-z0-9]+/;

export function tokenise(query: string): string[] {
  return query
    .toLowerCase()
    .split(WORD)
    .filter((t) => t.length > 0);
}

/**
 * Score one candidate's terms against the query tokens.
 *
 * Every token must match something or the candidate is out, so "left kidney"
 * cannot match a part that only knows about kidneys. Whole-word matches beat
 * prefixes, and a match on the first term beats one further along, so "heart"
 * ranks "Heart" above "Wall of left atrium of heart".
 */
export function scoreTerms(terms: readonly string[], tokens: readonly string[]): number {
  if (tokens.length === 0) return 0;
  let total = 0;
  for (const token of tokens) {
    let best = 0;
    for (let i = 0; i < terms.length; i++) {
      const term = terms[i]!;
      let s = 0;
      if (term === token) s = 10;
      else if (term.startsWith(token)) s = 6;
      else if (term.includes(token)) s = 3;
      if (s === 0) continue;
      // earlier terms carry the name's subject
      s += Math.max(0, 3 - i);
      if (s > best) best = s;
    }
    if (best === 0) return 0; // an unmatched token rules the candidate out
    total += best;
  }
  // shorter names are more likely to be the thing itself rather than a part of it
  return total - Math.min(4, terms.length * 0.25);
}

export function search(catalog: Catalog, query: string, options: SearchOptions = {}): SearchHit[] {
  const tokens = tokenise(query);
  if (tokens.length === 0) return [];
  const limit = options.limit ?? 20;
  const systems = options.systems;
  const hits: SearchHit[] = [];

  for (const system of catalog.systems) {
    if (systems && !systems.has(system.id)) continue;
    const score = scoreTerms(system.name.toLowerCase().split(WORD).filter(Boolean), tokens);
    // a system is a big, useful answer: nudge it up
    if (score > 0) hits.push({ group: { kind: "system", id: system.id, name: system.name, parts: system.parts }, score: score + 2, count: system.parts.length });
  }

  for (const concept of catalog.concepts) {
    if (concept.parts.length < 2) continue; // a one-part concept is better answered by the part itself
    const score = scoreTerms(concept.terms, tokens);
    if (score <= 0) continue;
    const parts = systems ? concept.parts.filter((id) => systems.has(catalog.byId.get(id)?.system ?? "")) : concept.parts;
    if (parts.length === 0) continue;
    hits.push({ group: { kind: "concept", id: concept.id, name: concept.name, parts }, score: score + 1, count: parts.length });
  }

  for (const part of catalog.parts) {
    if (systems && !systems.has(part.system)) continue;
    const score = scoreTerms(part.terms, tokens);
    if (score > 0) hits.push({ group: { kind: "part", id: part.id, name: part.name, parts: [part.id] }, score, count: 1 });
  }

  hits.sort((a, b) => b.score - a.score || a.group.name.length - b.group.name.length || a.group.name.localeCompare(b.group.name));
  return hits.slice(0, limit);
}

/**
 * The browse list when nothing is typed: systems, each with the concepts under
 * it that cover more than one part, so there is always something to drill into.
 */
export function browseSystems(catalog: Catalog): { system: Catalog["systems"][number]; concepts: Group[] }[] {
  const bySystem = new Map<string, Group[]>();
  for (const concept of catalog.concepts) {
    if (concept.parts.length < 2) continue;
    const system = catalog.byId.get(concept.parts[0]!)?.system;
    if (!system) continue;
    // a concept spanning systems belongs to the one holding most of it
    const counts = new Map<string, number>();
    for (const id of concept.parts) {
      const s = catalog.byId.get(id)?.system;
      if (s) counts.set(s, (counts.get(s) ?? 0) + 1);
    }
    let owner = system;
    let most = 0;
    for (const [s, n] of counts) if (n > most) ((most = n), (owner = s));
    let list = bySystem.get(owner);
    if (!list) bySystem.set(owner, (list = []));
    list.push({ kind: "concept", id: concept.id, name: concept.name, parts: concept.parts });
  }
  for (const list of bySystem.values()) list.sort((a, b) => b.parts.length - a.parts.length || a.name.localeCompare(b.name));
  // outermost first: skin, then muscle, then bone. That is the order a person
  // peels a body in, and it matches the `layer` the recipe assigns each system.
  return [...catalog.systems]
    .sort((a, b) => a.layer - b.layer || a.name.localeCompare(b.name))
    .map((system) => ({ system, concepts: bySystem.get(system.id) ?? [] }));
}
